<template>
  <div class="my-quiz-answers">
    <div class="container">
      <div v-if="isLoadingMyQuizAnswers">
        <SkeletonLoadingBars v-for="index in 2" :key="index" />
      </div>
      <div v-if="isShowQuizList" class="quiz-slide-content">
        <div v-for="(quizAnswer, index) in myQuizAnswers" :key="quizAnswer.id">
          <QuizItemReview
            :quizAnswer="quizAnswer"
            :order="index + 1"
            :totalQuiz="totalQuiz"
          />
        </div>
        <CTAAttendedPresentation />
      </div>
      <EmptyQuizAnswer
        v-if="isShowEmptyScreen"
        lookbackTab="answers"
        :msg="emptyQuizMsg"
      />
    </div>
  </div>
</template>

<script>
import AnswersAPI from '@/api/answers'
import QuizStatuses from '@/constants/QuizStatuses'
import storage from '@/services/storage'
import * as Sentry from '@sentry/vue'
import QuizItemReview from './QuizItemReview.vue'
import EmptyQuizAnswer from './EmptyQuizAnswer.vue'
import SkeletonLoadingBars from './SkeletonLoadingBars.vue'
import CTAAttendedPresentation from '../attended-presentations/CTAAttendedPresentation.vue'

const ATTENDED_PRESENTATION_DETAIL = 'attended-presentation-detail'

export default {
  components: {
    QuizItemReview,
    EmptyQuizAnswer,
    SkeletonLoadingBars,
    CTAAttendedPresentation,
  },
  props: {
    slide: {
      type: Object,
      required: true,
    },
    slideIndex: {
      type: Number,
      required: true,
    },
    slides: {
      type: Array,
      required: true,
    },
    presentation: {
      type: Object,
      required: true,
    },
    audienceId: {
      type: String,
      required: true,
    },
    activeTabView: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoadingMyQuizAnswers: false,
      isQuizEnd: false,
      myQuizAnswers: [],
      isMyQuizAnswersLoaded: false,
    }
  },
  computed: {
    currentSlideId() {
      return this.slide && this.slide.id
    },
    totalQuiz() {
      return this.myQuizAnswers && this.myQuizAnswers.length
    },
    lastQuizSlideIndex() {
      const slideList = this.slides || []

      const reverseListQuizIndex = [...slideList]
        .reverse()
        .findIndex((item) => item.type === 'pickAnswer')

      if (reverseListQuizIndex === -1) {
        return -1
      }

      return slideList.length - reverseListQuizIndex - 1
    },
    isPassedLastQuiz() {
      let passedLastQuizQuestion = false

      if (this.lastQuizSlideIndex === -1) {
        // no quiz, set true if current slide index is last slide
        passedLastQuizQuestion = this.slideIndex === this.slides.length - 1
      } else if (this.slideIndex === this.lastQuizSlideIndex) {
        // has quiz, set true if last quiz question is end
        const currentSlideStatus = this.slide && this.slide.quizStatus
        passedLastQuizQuestion = currentSlideStatus === QuizStatuses.RESULT_SCREEN
      } else {
        // otherwise, set true if slide index is greater than last quiz question
        passedLastQuizQuestion = this.slideIndex >= this.lastQuizSlideIndex
      }

      return passedLastQuizQuestion
    },
    isShowQuizList() {
      return this.isMyQuizAnswersLoaded && !!this.totalQuiz
    },
    isShowEmptyScreen() {
      return !this.isShowQuizList
        && !this.isLoadingMyQuizAnswers
    },
    emptyQuizMsg() {
      return this.isMyQuizAnswersLoaded && this.totalQuiz === 0 ? this.$t('no_answer') : ''
    },
    isGetAnswers() {
      return (this.activeTabView === 'lookback' || this.$route.query[ATTENDED_PRESENTATION_DETAIL]) && this.isQuizEnd
    },
  },
  watch: {
    isGetAnswers: {
      handler(value) {
        if (value) {
          this.checkForApiRequest()
        }
      },
      immediate: true,
    },
    isPassedLastQuiz: {
      handler(value) {
        if (value) {
          this.isQuizEnd = value
          storage.setValueByPresentation(this.presentation.id, 'isQuizEnd', value)
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadMyQuizAnswersState()
  },
  methods: {
    loadMyQuizAnswersState() {
      const isPassedLastQuiz = storage.getValueByPresentation(this.presentation.id, 'isQuizEnd')

      if (!isPassedLastQuiz) return

      this.isQuizEnd = true
      this.$nextTick(() => {
        this.checkForApiRequest()
      })
    },
    async getMyQuizAnswers() {
      try {
        const answers = await AnswersAPI.getMyQuizAnswers({
          presentationId: this.presentation && this.presentation.id,
          audienceId: this.audienceId,
        })

        this.myQuizAnswers = Array.isArray(answers) ? [...answers] : []
        this.isMyQuizAnswersLoaded = true
      } catch (error) {
        Sentry.captureException(error)
        // console.error('Error while fetching my quiz answers', error)
      } finally {
        this.isLoadingMyQuizAnswers = false
      }
    },
    async checkForApiRequest() {
      if (this._shouldLoadQuizAnswers()) {
        this.isLoadingMyQuizAnswers = true
        await this.getMyQuizAnswers()
      }
    },
    _shouldLoadQuizAnswers() {
      return (
        (this.activeTabView === 'lookback' || this.$route.query[ATTENDED_PRESENTATION_DETAIL])
        && this.isQuizEnd
        && !this.isMyQuizAnswersLoaded
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.my-quiz-answers {
  margin-top: 24px;
  margin-bottom: 24px;

  .container {
    max-width: 558px;
    margin: 0 auto;
  }
}
</style>
